import { useState, useRef, useEffect } from "react";
import { motion } from "motion/react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Plus } from "lucide-react";
import { EASE_EXPO, KineticHeading, SPRING_POP } from "../motion/primitives";
import { faqItems } from "../../data/faq-data";

/** Questions to EXCLUDE from landing page (too detailed, covered by the tool/other sections) */
const EXCLUDED = new Set([
  "What is an apostille and do I need one?",
  "Can I apply after arriving in Austria?",
  "How much money do I need to prove?",
  "What is an Aufenthaltstitel?",
  "What if my application is rejected?",
]);

const studentFAQs = faqItems.filter((item) => !item.audience && !EXCLUDED.has(item.question));
const parentFAQs = faqItems.filter((item) => item.audience === "parents");
const TABS = [
  { id: "students", label: "For students", items: studentFAQs },
  { id: "parents", label: "For parents", items: parentFAQs },
] as const;

const INK = "#0A0F0E";
const BRAND_DARK = "#00332F";
const TEAL = "#2EE6D6";
const SECONDARY = "#46504D";

/**
 * Landing page FAQ: a curated subset of faq-data.ts, same SEO-safe pattern as
 * /faq (answers always in the DOM, opened with CSS max-height, never
 * conditionally rendered). The plus turns into a cross as a row opens.
 */

export function FAQRow({
  question,
  answer,
  index,
}: {
  question: string;
  answer: string[];
  index: number;
}) {
  const [open, setOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState(0);

  // Measure when the row opens, not at mount: rows in a hidden tab (the
  // parents' answers) measure 0 while hidden and would open to nothing.
  useEffect(() => {
    if (open && contentRef.current) {
      setMaxHeight(contentRef.current.scrollHeight);
    }
  }, [open, answer]);

  useEffect(() => {
    const handleResize = () => {
      if (contentRef.current) {
        setMaxHeight(contentRef.current.scrollHeight);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.6, delay: index * 0.05, ease: EASE_EXPO }}
      className="border-b border-[#E0E3DE]"
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 py-5 sm:py-6 text-left cursor-pointer group min-h-[48px]"
        aria-expanded={open}
      >
        <span
          className="pr-2 text-[18px] font-extrabold leading-snug tracking-[-0.015em] transition-colors group-hover:text-[#00786F] sm:text-[19px]"
          style={{ color: INK }}
        >
          {question}
        </span>
        <motion.span
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full"
          animate={{ rotate: open ? 45 : 0, backgroundColor: open ? TEAL : "#F2F2ED" }}
          transition={{ duration: 0.35, ease: EASE_EXPO }}
          style={{ color: BRAND_DARK }}
          aria-hidden="true"
        >
          <Plus size={18} strokeWidth={2.6} />
        </motion.span>
      </button>

      <div
        ref={contentRef}
        className="overflow-hidden transition-[max-height,opacity] duration-300 ease-out"
        style={{
          maxHeight: open ? `${maxHeight}px` : "0px",
          opacity: open ? 1 : 0,
        }}
      >
        <div className="pb-6 space-y-3">
          {answer.map((paragraph, i) => (
            <p
              key={i}
              className="max-w-[46rem] text-[16.5px] leading-[1.7]"
              style={{ color: SECONDARY }}
            >
              {paragraph}
            </p>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

export function LandingFAQ() {
  const location = useLocation();
  const [tab, setTab] = useState<"students" | "parents">("students");
  // /#parents opens the parents' answers.
  useEffect(() => {
    if (location.hash === "#parents") setTab("parents");
  }, [location.hash]);

  return (
    <section id="faq" className="scroll-mt-24" style={{ backgroundColor: "#FFFFFF" }}>
      <span id="parents" className="block scroll-mt-24" aria-hidden="true" />
      <div className="mx-auto max-w-[1120px] px-5 py-20 sm:px-8 md:py-24">
        <div className="mb-8 flex flex-col gap-5 md:mb-10 md:flex-row md:items-end md:justify-between">
          <div>
            <KineticHeading
              text="What students and parents ask"
              className="font-black leading-[0.95] tracking-[-0.04em] [text-wrap:balance]"
              style={{ fontSize: "var(--text-h2)", color: INK }}
            />
            <p className="mt-4 max-w-lg text-[18px] leading-relaxed" style={{ color: SECONDARY }}>
              For students and for the parents who usually pay for the move.
            </p>
          </div>
          <div className="flex shrink-0 flex-col gap-2 text-[16px] font-bold md:items-end">
            <Link to="/faq" className="group inline-flex items-center gap-1.5" style={{ color: BRAND_DARK }}>
              <span className="underline decoration-2 underline-offset-[6px] group-hover:decoration-[#2EE6D6]">All questions</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
            </Link>
            <Link to={{ pathname: "/community", hash: "#ask" }} className="group inline-flex items-center gap-1.5" style={{ color: BRAND_DARK }}>
              <span className="underline decoration-2 underline-offset-[6px] group-hover:decoration-[#2EE6D6]">Ask the community</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </div>
        </div>

        <div className="mb-6 inline-flex rounded-full p-1" style={{ backgroundColor: "#F2F2ED" }} role="tablist" aria-label="Questions for">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              id={`faq-tab-${t.id}`}
              aria-selected={tab === t.id}
              aria-controls={`faq-panel-${t.id}`}
              onClick={() => setTab(t.id)}
              className="relative rounded-full px-5 py-2.5 text-[15px] font-bold"
              style={{ color: tab === t.id ? BRAND_DARK : "#69716E" }}
            >
              {tab === t.id && <motion.span layoutId="faq-tab" className="absolute inset-0 rounded-full" style={{ backgroundColor: TEAL }} transition={SPRING_POP} />}
              <span className="relative">{t.label}</span>
            </button>
          ))}
        </div>

        {/* Both lists stay in the DOM for search engines; only the active one shows. */}
        {TABS.map((t) => (
          <div key={t.id} id={`faq-panel-${t.id}`} role="tabpanel" aria-labelledby={`faq-tab-${t.id}`} hidden={tab !== t.id} className="border-t border-[#E0E3DE]">
            {t.items.map((item, i) => (
              <FAQRow key={item.question} question={item.question} answer={item.answer} index={i} />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}
